import React, { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import styles from "./offline.module.less";
import ChatState from "@/types/chat/State";
import { connect } from "dva";
import ConnectState from "@jd/jdee.im.sdk/lib/es/enum/ConnectState";
import IconFont from "@/components/icon";
import { FocusSDK as focusSDK, isFocusEnv } from "@/utils";
import { Button, Modal } from "antd";
import NetworkCheck from "@/utils/NetworkCheck";
import { getNetCheckUrl } from "@/config/config";
import GlobalContext, { GlobalContextConfig } from "@/context/GlobalContext";
import AppRuntimeEnv from "@/types/AppRuntimeEnv";
import sentryManual from "@/utils/sentryManual";
import { RegistryConfigData } from "@/types/LocalConfig";

function mapStateToProps({ chat }: { chat: ChatState }) {
  const { connectState } = chat;
  return { connectState };
}

interface OfflineProps {
  connectState: boolean;
  currentNetOffline: boolean;
  appRuntimeEnv: AppRuntimeEnv;
  localConfigData: RegistryConfigData;
}

type IProps = Readonly<OfflineProps & ChatState & WithTranslation>;

function sleepTime(timeout: number) {
  return new Promise((resolve) => {
    setTimeout(resolve, timeout);
  });
}

function OfflineTip(props: IProps) {
  const { t, currentNetOffline, appRuntimeEnv, localConfigData } = props;
  const isPrivateNet = focusSDK.isPrivateNet();
  const otherNetCheckIdRef = useRef("");

  const egovFirst = useMemo(() => {
    const settings = localConfigData?.settings;
    return settings?.egovFirst !== false;
  }, [localConfigData]);

  let btnName = "互联网";
  // 当前为互联网，切换至政务外网;
  if (!isPrivateNet) {
    btnName = "政务外网";
  }
  const [btnChangeDis, setBtnChangeDis] = useState(false);

  const onlineListener = useCallback(() => {
    setBtnChangeDis(true);
    const otherNetworkCheck = NetworkCheck.getById(otherNetCheckIdRef.current);
    otherNetworkCheck?.stopCheck();
  }, []);

  const changeNet = () => {
    sentryManual.captureMessage(`当前网络不可用切换至${btnName}`, "net-change-manual");
    Modal.confirm({
      title: "提示",
      content: `是否切换至${btnName}，确认之后应用将会重启。`,
      onOk: () => {
        focusSDK.setConfigData(`settings|egovFirst`, !egovFirst);
        focusSDK.setConfigData(`settings|netSettingChanged`, true);
        focusSDK.on("configDataChange", focusSDK.relaunchApp);
      },
      okText: "确定",
      cancelText: "取消"
    });
  };

  useEffect(() => {
    let networkCheck: any;
    let timer: any;
    if (isFocusEnv()) {
      const fn = async () => {
        const app_runtime = (window as any)?._BTS_CONFIG?.REACT_APP_RUNTIME || process.env.REACT_APP_RUNTIME;
        setBtnChangeDis(false);
        if (currentNetOffline && app_runtime === "mebjJZB" && appRuntimeEnv?.key === "prod") {
          focusSDK.printLog(`---出现横幅断网提示,navigator.onLine: ${navigator.onLine}---`);
          // 5秒后开始检测其它网络
          timer = setTimeout(() => {
            clearTimeout(timer);
            networkCheck = NetworkCheck.getInstance({
              url: getNetCheckUrl(true, appRuntimeEnv?.key),
              notChangeNavigatorOnline: true
            });
            otherNetCheckIdRef.current = networkCheck.getId();
            networkCheck.w[networkCheck.getId() + "_onLine"] = false;
            if (!networkCheck?.isStartCheck()) {
              networkCheck?.startCheck();
            }
            networkCheck?.on("online", onlineListener);
          }, 5000);
        }
      };
      fn();
    }
    return () => {
      clearTimeout(timer);
      networkCheck?.stopCheck();
      networkCheck?.off("online", onlineListener);
    };
  }, [currentNetOffline, onlineListener, appRuntimeEnv?.key]);

  return currentNetOffline ? (
    <div
      className={styles["offline-wrap"]}
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <p className={styles["offline-content"]}>
        <IconFont className={styles["icon-close"]} type="iconjs_ic_error" />
        <span className={styles["content-disabled"]}>
          {t("connection-status.network-disabled")}
        </span>
        {isFocusEnv() && btnChangeDis && (
          <Button className={styles["change-btn"]} type="text" onClick={changeNet}>
            切换至{btnName}
          </Button>
        )}
      </p>
    </div>
  ) : null;
}

export default connect(mapStateToProps)(withTranslation("chat")(OfflineTip));
